import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../../hooks/reduxHooks';
import {
  clearCompletedStatus,
  clearFailedStatus,
  selectHeadCompletionForGlobalUi,
  selectHeadFailure,
} from '../../../store/slices/dmUnlockSlice';

const TOAST_DURATION_MS = 8000;

const DMUnlockCompletionToast: React.FC = () => {
  const dispatch = useAppDispatch();
  const headCompletion = useAppSelector(selectHeadCompletionForGlobalUi);
  const headFailure = useAppSelector(selectHeadFailure);
  const [isSuccessVisible, setIsSuccessVisible] = useState(true);
  const [isFailedVisible, setIsFailedVisible] = useState(true);

  useEffect(() => {
    if (headCompletion) {
      setIsSuccessVisible(true);
      const timer = setTimeout(() => {
        setIsSuccessVisible(false);
        dispatch(clearCompletedStatus()); // shifts queue head
      }, TOAST_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [headCompletion?.batch_id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (headFailure) {
      setIsFailedVisible(true);
      const timer = setTimeout(() => {
        setIsFailedVisible(false);
        dispatch(clearFailedStatus());
      }, TOAST_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [headFailure?.batchId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {headCompletion && isSuccessVisible && (
        <div className="fixed top-20 right-4 z-50 max-w-md animate-slide-in">
          <div className="bg-white border-l-4 border-green-500 rounded-lg shadow-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">People enrichment complete!</h4>
                  <p className="text-sm text-gray-700">
                    {headCompletion.succeeded === 0 ? (
                      <>
                        No new emails found for this batch ({headCompletion.total}{' '}
                        {headCompletion.total === 1 ? 'person' : 'people'}). You can try again or pick
                        other contacts.
                      </>
                    ) : (
                      <>
                        {headCompletion.succeeded} of {headCompletion.total}{' '}
                        {headCompletion.total === 1 ? 'person' : 'people'} with a new email.
                      </>
                    )}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSuccessVisible(false);
                  dispatch(clearCompletedStatus());
                }}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      {headFailure && isFailedVisible && (
        <div className="fixed top-20 right-4 z-50 max-w-md animate-slide-in">
          <div className="bg-white border-l-4 border-red-500 rounded-lg shadow-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">People enrichment failed</h4>
                  <p className="text-sm text-gray-700">
                    Could not reach the service. Please try again.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsFailedVisible(false);
                  dispatch(clearFailedStatus());
                }}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DMUnlockCompletionToast;
